// WbContractsPage — /weighbridge/pricing/contracts (admin only).
//
// Phase C4 Group 2. Lists tenant contracts from /api/v1/wb/contracts.
// Non-paginated. Status filter drives the server-side query param
// (`status=`); the free-text search filters loaded rows client-side
// on customer name / contract name. Row click navigates to the
// detail/edit page; "+ Add Contract" routes to /new.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileSignature, Trash2 } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useDeleteContract,
  useWbContracts,
  type ContractStatus,
  type WbContractListItem,
} from '../hooks/useWbContracts';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbContractsPage.module.css';

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<
  ContractStatus,
  'muted' | 'success' | 'warning' | 'danger'
> = {
  draft: 'muted',
  active: 'success',
  expired: 'warning',
  cancelled: 'danger',
};

export function WbContractsPage(): JSX.Element {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<'' | ContractStatus>('');
  const [search, setSearch] = useState('');

  // Server-side filter on status only; search is client-side (the server
  // does not expose a free-text contract search).
  const params = useMemo(
    () => (statusFilter ? { status: statusFilter } : {}),
    [statusFilter],
  );
  const contractsQuery = useWbContracts(params);
  const contracts = contractsQuery.data?.contracts ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return contracts;
    return contracts.filter(
      (c) =>
        c.customer.name.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q),
    );
  }, [contracts, search]);

  // Delete flow — single-row modal.
  const [pendingDelete, setPendingDelete] =
    useState<WbContractListItem | null>(null);
  const deleteMut = useDeleteContract(pendingDelete?.enc_id ?? '');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function handleRowDelete(row: WbContractListItem) {
    setDeleteError(null);
    setPendingDelete(row);
  }

  function handleDeleteConfirm() {
    if (!pendingDelete) return;
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setPendingDelete(null);
      },
      onError: (err) => {
        setDeleteError(extractErrorMessage(err));
        setPendingDelete(null);
      },
    });
  }

  const hasAnyFilter = statusFilter !== '' || search.trim() !== '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Contracts"
        subtitle="Per-customer pricing agreements layered over the catalogue defaults."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/pricing/contracts/new')}
          >
            Add Contract
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by customer or contract name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search contracts"
        />
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as '' | ContractStatus)
          }
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {contractsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(contractsQuery.error)}
        </p>
      ) : null}
      {deleteError ? <p className={styles.error}>{deleteError}</p> : null}

      <Card flush>
        {contractsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading contracts" />
          </div>
        ) : filtered.length === 0 ? (
          hasAnyFilter ? (
            <EmptyState
              icon={FileSignature}
              title="No matching contracts"
              description="No contracts match the current filters. Try clearing them."
            />
          ) : (
            <EmptyState
              icon={FileSignature}
              title="No contracts yet"
              description="Add a contract to start applying per-customer pricing."
              action={
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() =>
                    navigate('/weighbridge/pricing/contracts/new')
                  }
                >
                  Add Contract
                </Button>
              }
            />
          )
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contract name</th>
                <th>Valid from</th>
                <th>Valid to</th>
                <th>Status</th>
                <th className={styles.numCell}>Lines</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.enc_id}
                  onClick={() =>
                    navigate(
                      `/weighbridge/pricing/contracts/${encodeURIComponent(
                        c.enc_id,
                      )}`,
                    )
                  }
                >
                  <td>{c.customer.name}</td>
                  <td>{c.name}</td>
                  <td>{c.valid_from}</td>
                  <td>{c.valid_to}</td>
                  <td>
                    <Badge variant={STATUS_VARIANT[c.status]}>
                      {STATUS_LABEL[c.status]}
                    </Badge>
                  </td>
                  <td className={styles.numCell}>{c.line_count}</td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/weighbridge/pricing/contracts/${encodeURIComponent(
                            c.enc_id,
                          )}`,
                        );
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      aria-label={`Delete contract ${c.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowDelete(c);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmModal
        open={pendingDelete !== null}
        title={
          pendingDelete?.status === 'active'
            ? 'Delete active contract?'
            : 'Delete contract?'
        }
        message={
          pendingDelete?.status === 'active' ? (
            <p>
              This contract is active. Deleting it will remove pricing
              overrides for this customer. Continue?
            </p>
          ) : (
            <p>
              Delete <strong>{pendingDelete?.name ?? 'this contract'}</strong>?
              This cannot be undone.
            </p>
          )
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setPendingDelete(null)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
